import "./videoUploader.scss";
import { ComponentRegistry } from '@ts/utils/ComponentRegistry';
import { Thermo } from '@ts/components/Thermo';

type VideoUploaderOptions = {
  uploadUrl?: string;
  fileSizeLimit?: number;
  acceptedTypes?: string[];
  inputFileName?: string;
  token?: string;
  onStart?: () => void;
  onSuccess?: (data?: any) => void;
  onError?: (error: string) => void;
};

type ProcessingUrlResponse = {
  url: string;
  videoId: string;
};

type ProcessingStatusResponse = {
  status: 'Complete' | 'Error' | 'InProgress';
};

const ACCEPTED_FILE_TYPES = [
  "video/mp4",
  "video/mov",
  "video/quicktime",
  "video/x-m4v",
  "video/x-ms-wmv",
  "video/x-msvideo",
  "video/webm"
];

const ERROR_MESSAGES = {
  invalidFileType: "Please use a valid video file (.mp4, .m4v, .mov, .wmv, .avi, and .webm)",
  exceedsFileSizeLimit: "Video file is too large, maximum file size is 10GB"
};

export class VideoUploader {
  private static players: Map<string, any> = new Map();
  private fileSizeLimit = 10000000000;

  private readonly id: string;
  private static registry = new ComponentRegistry<VideoUploader>('video-uploader');
  private readonly options: Required<VideoUploaderOptions>;
  
  private trigger: HTMLButtonElement | HTMLAnchorElement;
  private input: HTMLInputElement | null = null;
  private gaugeElement: HTMLDivElement | null = null;

  private constructor(
    trigger: HTMLButtonElement | HTMLAnchorElement,
    options: VideoUploaderOptions
  ) {

    if (!options.uploadUrl) {
      throw new Error('VideoUploader: uploadUrl is required');
    }

    this.trigger = trigger;
    this.id = VideoUploader.registry.register(this.trigger, this);

    this.options = {
      uploadUrl: options.uploadUrl,
      fileSizeLimit: options.fileSizeLimit ?? this.fileSizeLimit,
      acceptedTypes: options.acceptedTypes ?? ACCEPTED_FILE_TYPES,
      inputFileName: options.inputFileName ?? "video",
      token: options.token ?? '',
      onStart: options.onStart ?? (() => {}),
      onSuccess: options.onSuccess ?? (() => {}),
      onError: options.onError ?? (() => {}),
    };

    this.init();
  }

  private init = (): void => {
    this.createWrapper();
    this.createFileInput();
    this.bindTriggerEvents();
  };

  private createWrapper = (): void => {
    const wrapper = document.createElement("div");
    wrapper.classList.add("video-uploader-wrapper");
    wrapper.id = this.id;

    this.trigger.parentNode?.insertBefore(wrapper, this.trigger);
    wrapper.appendChild(this.trigger);
  };

  private createFileInput = (): void => {
    this.input = document.createElement("input");
    this.input.type = "file";
    this.input.name = this.options.inputFileName;
    this.input.accept = this.options.acceptedTypes.join(",");
    this.input.style.display = "none";

    const wrapper = document.getElementById(this.id);
    if (wrapper) {
      wrapper.appendChild(this.input);
    }

    this.input.addEventListener("change", this.handleFileChange);
  };

  private bindTriggerEvents = (): void => {
    this.trigger.addEventListener("click", this.handleTriggerClick);
  };

  private handleTriggerClick = (e: Event): void => {
    e.preventDefault();
    this.input?.click();
  };

  private handleFileChange = async (e: Event): Promise<void> => {
    const target = e.target as HTMLInputElement;
    const file = target.files?.[0];

    if (!file) {
      return;
    }

    const fileSizeValidation = this.validateFileSize(file.size);
    if (!fileSizeValidation.status) {
      this.options.onError(fileSizeValidation.error!);
      return;
    }

    const fileTypeValidation = this.validateFileType(file.type);
    if (!fileTypeValidation.status) {
      this.options.onError(fileTypeValidation.error!);
      return;
    }

    this.disableTrigger();
    this.options.onStart();

    try {
      const data = await this.getProcessingUrl(file.size);
      await this.processVideo(data.url, file);
      this.setMediaId(data.videoId);
      this.options.onSuccess(data);
    } catch (error) {
      this.options.onError(error instanceof Error ? error.message : String(error));
    } finally {
      this.enableTrigger();
      if (this.input) {
        this.input.value = "";
      }
    }
  };

  private validateFileSize = (fileSize: number): { status: boolean; error?: string } => {
    if (fileSize > this.options.fileSizeLimit) {
      return {
        status: false,
        error: ERROR_MESSAGES.exceedsFileSizeLimit
      };
    }
    return { status: true };
  };

  private validateFileType = (fileType: string): { status: boolean; error?: string } => {
    if (!this.options.acceptedTypes.includes(fileType)) {
      return {
        status: false,
        error: ERROR_MESSAGES.invalidFileType
      };
    }
    return { status: true };
  };

  private setMediaId = (mediaId: string): void => {
    const videoIdInput = document.getElementById('VideoId') as HTMLInputElement;
    const videoStatusInput = document.getElementById('VideoStatus') as HTMLInputElement;
    if (!videoStatusInput || !videoIdInput) return;
    videoStatusInput.value = 'InProgress';
    videoIdInput.value = mediaId;
  }

  private getMediaUploaderContainer = (): HTMLElement | null => {
    return this.trigger.closest('.media-upload-container');
  }

  private setContainerViewMode = (mode: 'Uploading' | 'Complete' | 'Error' | 'InProgress'): void => {
    const container = this.getMediaUploaderContainer();
    if (container) {
      container.setAttribute('data-view-mode', mode);
    }
  }

  private getProcessingUrl = async (fileSize: number): Promise<ProcessingUrlResponse> => {
      const response = await fetch(this.options.uploadUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({ size: fileSize })
      });
    
      if (!response.ok) {
        console.log('Failed to get processing URL', response.statusText);
      }
      return await response.json() as ProcessingUrlResponse;
  };

  private processVideo = async (videoUploadUrl: string, file: File): Promise<void> => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      // Track upload progress
      xhr.upload.addEventListener("progress", (evt: ProgressEvent) => {
        if (evt.lengthComputable) {
          this.updateProgressGauge(evt.loaded, evt.total);
        }
      });

      xhr.addEventListener("load", () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          this.removeProgressGauge();
          this.setContainerViewMode('InProgress');
          resolve();
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      });

      xhr.addEventListener("error", () => {
        this.removeProgressGauge();
        this.setContainerViewMode('Error');
        reject(new Error("Upload failed"));
      });

      xhr.addEventListener("abort", () => {
        reject(new Error("Upload aborted"));
      });

      xhr.open("PATCH", videoUploadUrl);
      xhr.setRequestHeader('Tus-Resumable', '1.0.0');
      xhr.setRequestHeader('Upload-Offset', '0');
      xhr.setRequestHeader('Content-Type', 'application/offset+octet-stream');
      xhr.setRequestHeader('Accept', 'application/vnd.vimeo.*+json;version=3.4');
      
      this.setContainerViewMode('Uploading');
      this.createProgressGauge(file.size);
      xhr.send(file);
    });
  }

  private createProgressGauge = (total: number): void => {
    this.gaugeElement = document.createElement("div");
    this.gaugeElement.className = "video-uploader-progress";
    this.gaugeElement.innerHTML = `
      <div id="uploader-status-bar" class="thermo" data-current-value="0" data-max-value="100"></div>
      <div class="progress-text">Uploading: 0%</div>
    `;

    const wrapper = document.getElementById(this.id);
    if (wrapper) {
      wrapper.appendChild(this.gaugeElement);
    }
    const stepThermometer = Thermo.create(".thermo");
  };

  private updateProgressGauge = (currentValue: number, totalValue: number): void => {
    if (!this.gaugeElement) return;

    const thermo = Thermo.getThermo("uploader-status-bar");
    thermo?.update((currentValue / totalValue) * 100);
    const progressText = this.gaugeElement.querySelector(".progress-text") as HTMLElement;

    if (progressText) {
      const percentage = Math.round((currentValue / totalValue) * 100);
      progressText.textContent = `Uploading: ${percentage}%`;
    }
  };

  private removeProgressGauge = (): void => {
    if (this.gaugeElement) {
      this.gaugeElement.remove();
      this.gaugeElement = null;
    }
  };

  private disableTrigger = (): void => {
    this.trigger.setAttribute("disabled", "true");
    this.trigger.classList.add("disabled");
  };

  private enableTrigger = (): void => {
    this.trigger.removeAttribute("disabled");
    this.trigger.classList.remove("disabled");
  };

  public static getProcessingStatus = async (processingStatusUrl: string): Promise<any> => {

  };

  public static create = (
    target: string | HTMLButtonElement | HTMLAnchorElement | NodeListOf<HTMLElement> | HTMLElement[],
    options: VideoUploaderOptions = {}
  ): VideoUploader[] | null => {
    const triggers = ComponentRegistry.processTargets<VideoUploader, HTMLElement>(
        VideoUploader.registry,
        target,
        [HTMLButtonElement, HTMLAnchorElement]
    ) as (HTMLButtonElement | HTMLAnchorElement)[];

    if (!triggers.length) return null;
    return triggers.map(trigger => new VideoUploader(trigger, options));
  };

  public static getVideoUploader(id: string): VideoUploader | null {
      return VideoUploader.registry.get(id) ?? null;
  }

  public getId = (): string => this.id;

  public destroy = (): void => {
    if (this.input) {
      this.input.removeEventListener("change", this.handleFileChange);
      this.input.remove();
    }

    this.trigger.removeEventListener("click", this.handleTriggerClick);
    this.removeProgressGauge();

    const wrapper = document.getElementById(this.id);
    if (wrapper) {
      wrapper.replaceWith(this.trigger);
    }
    VideoUploader.registry.unregister(this.id);
  };
}